import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const APP_ORIGIN = window.location.origin;
const APP_BASE = (process.env.PUBLIC_URL || "").replace(/\/$/, "");

/** Turns a relative path into an absolute URL. Absolute URLs pass through. */
function toAbsoluteUrl(path: string): string {
    if (/^https?:\/\//.test(path)) return path;
    return `${APP_ORIGIN}${APP_BASE}${path}`;
}

interface SeoProps {
    title: string;
    description?: string;
    /** Relative ("/") or absolute URL. Defaults to the current pathname. */
    canonical?: string;
    /** Relative ("/images/foo.png") or absolute URL. */
    image?: string;
    ogType?: "article" | "website";
    /** Adds noindex/nofollow for private or partial pages. */
    noindex?: boolean;
}

/**
 * Drop this into any page to set its meta tags.
 * Only render it once data is loaded — it also emits #seo-ready,
 * which Rendertron waits for before snapshotting the page.
 */
const Seo: React.FC<SeoProps> = ({ title, description, canonical, image, ogType = "website", noindex = false }) => {
    const location = useLocation();

    const canonicalUrl = canonical
        ? toAbsoluteUrl(canonical)
        : `${APP_ORIGIN}${APP_BASE}${location.pathname}`;

    const imageUrl = image ? toAbsoluteUrl(image) : undefined;

    return (
        <>
            <Helmet>
                <title>{title}</title>
                {description && <meta name="description" content={description} />}
                <link rel="canonical" href={canonicalUrl} />
                {noindex && <meta name="robots" content="noindex, nofollow" />}

                {/* OpenGraph */}
                <meta property="og:title" content={title} />
                <meta property="og:type" content={ogType} />
                <meta property="og:url" content={canonicalUrl} />
                {description && <meta property="og:description" content={description} />}
                {imageUrl && <meta property="og:image" content={imageUrl} />}

                {/* Twitter / X */}
                <meta name="twitter:card" content={imageUrl ? "summary_large_image" : "summary"} />
                <meta name="twitter:title" content={title} />
                {description && <meta name="twitter:description" content={description} />}
                {imageUrl && <meta name="twitter:image" content={imageUrl} />}
            </Helmet>

            {/* Rendertron waits for this node before snapshotting. */}
            <div id="seo-ready" aria-hidden="true" style={{ display: "none" }} />
        </>
    );
};

export default Seo;
